import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './StudentDashboard.css';

type TabType = 'library' | 'schools' | 'downloads' | `school-${string}`;

interface ContentItem {
  id: string;
  school_id?: string;
  title: string;
  title_fa?: string;
  description?: string;
  description_fa?: string;
  r2_audio_path?: string;
  r2_slides_path?: string;
  r2_package_path?: string;
  r2_path?: string;
  sync_data?: string;
  duration_seconds?: number;
  slide_count?: number;
  content_type?: string;
  grade?: number;
  course?: string;
  topic_number?: number;
  file_size_bytes?: number;
  sort_order?: number;
  status: string;
  created_at: string;
  school_name?: string;
  school_name_fa?: string;
}

interface SchoolItem {
  id: string;
  name: string;
  name_fa?: string;
  description?: string;
  description_fa?: string;
  category?: string;
  subscriber_count: number;
  content_count: number;
  is_subscribed: boolean;
}

interface SubscriptionItem {
  id: string;
  school_id: string;
  school_name: string;
  school_name_fa?: string;
  school_description?: string;
  school_description_fa?: string;
  school_category?: string;
  content_count: number;
  created_at: string;
}

export function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isLoading, accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('library');
  const [subscribedSchools, setSubscribedSchools] = useState<SubscriptionItem[]>(() => {
    // Load from sessionStorage for instant render on re-mount (avoids flicker after navigation)
    try {
      const cached = sessionStorage.getItem('subscribedSchools');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  // Fetch subscribed schools for sidebar
  const fetchSubscribedSchools = useCallback(async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        const subs = data.data?.subscriptions || [];
        setSubscribedSchools(subs);
        try { sessionStorage.setItem('subscribedSchools', JSON.stringify(subs)); } catch {}
      }
    } catch (err) {
      console.error('Error fetching subscribed schools:', err);
    }
  }, [accessToken]);

  // Fetch subscriptions on mount
  useEffect(() => {
    fetchSubscribedSchools();
  }, [fetchSubscribedSchools]);

  // Listen for tab switch events from child components
  useEffect(() => {
    const handleSwitchTab = (e: CustomEvent<TabType>) => {
      setActiveTab(e.detail);
    };
    window.addEventListener('switchTab', handleSwitchTab as EventListener);
    return () => window.removeEventListener('switchTab', handleSwitchTab as EventListener);
  }, []);

  // Listen for subscription changes to refresh sidebar
  useEffect(() => {
    const handleSubscriptionChange = () => {
      fetchSubscribedSchools();
    };
    window.addEventListener('subscriptionChanged', handleSubscriptionChange);
    return () => window.removeEventListener('subscriptionChanged', handleSubscriptionChange);
  }, [fetchSubscribedSchools]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isRTL = i18n.language === 'fa';

  // Handle redirects in useEffect
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      }
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="sd-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  if (!user || user.role === 'teacher') {
    return (
      <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="sd-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="sd-header">
        <div className="sd-header-content">
          <a href="/" className="sd-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </a>
          <div className="sd-header-actions">
            <LanguageSwitcher />
            <button onClick={handleLogout} className="sd-logout-btn">
              {t('common.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="sd-layout">
        {/* Sidebar Navigation */}
        <aside className="sd-sidebar">
          <div className="sd-user-info">
            <div className="sd-user-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
            </div>
            <div className="sd-user-details">
              <span className="sd-user-name">{user.name || user.email}</span>
              <span className="sd-user-role">{t('auth.student')}</span>
            </div>
          </div>

          <nav className="sd-nav">
            <button
              className={`sd-nav-item ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => setActiveTab('library')}
            >
              <span className="sd-nav-icon">📚</span>
              <span className="sd-nav-label">{i18n.language === 'fa' ? 'کتابخانه من' : 'My Library'}</span>
            </button>
            
            {/* AI Curriculum - the main content browser */}
            <button
              className="sd-nav-item"
              onClick={() => navigate('/curriculum')}
            >
              <span className="sd-nav-icon">🎓</span>
              <span className="sd-nav-label">{i18n.language === 'fa' ? 'نصاب هوش مصنوعی' : 'AI Curriculum'}</span>
            </button>

            {/* Subscribed schools */}
            {subscribedSchools.filter(s => s.school_category !== 'curriculum').map(school => (
              <button
                key={school.school_id}
                className={`sd-nav-item ${activeTab === `school-${school.school_id}` ? 'active' : ''}`}
                onClick={() => setActiveTab(`school-${school.school_id}` as TabType)}
              >
                <span className="sd-nav-icon">🏫</span>
                <span className="sd-nav-label">{i18n.language === 'fa' ? (school.school_name_fa || school.school_name) : school.school_name}</span>
              </button>
            ))}
            
            <button
              className={`sd-nav-item ${activeTab === 'schools' ? 'active' : ''}`}
              onClick={() => setActiveTab('schools')}
            >
              <span className="sd-nav-icon">🔍</span>
              <span className="sd-nav-label">{i18n.language === 'fa' ? 'کشف مدارس' : 'Discover Schools'}</span>
            </button>
            <button
              className={`sd-nav-item ${activeTab === 'downloads' ? 'active' : ''}`}
              onClick={() => setActiveTab('downloads')}
            >
              <span className="sd-nav-icon">📥</span>
              <span className="sd-nav-label">{t('content.download')}</span>
            </button>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="sd-main">
          {activeTab === 'library' && <LibraryTab accessToken={accessToken} />}
          {activeTab === 'schools' && <SchoolsTab accessToken={accessToken} onSubscriptionChange={fetchSubscribedSchools} />}
          {activeTab === 'downloads' && <DownloadsTab accessToken={accessToken} />}
          {activeTab.startsWith('school-') && (
            <SchoolContentTab 
              accessToken={accessToken} 
              schoolId={activeTab.replace('school-', '')}
              school={subscribedSchools.find(s => s.school_id === activeTab.replace('school-', ''))}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// Library Tab - Content from subscribed schools
function LibraryTab({ accessToken }: { accessToken: string | null }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState<{ grade: number; course: string; items: ContentItem[] }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const SUBJECT_META: Record<string, { en: string; fa: string; icon: string; color: string }> = {
    physic:    { en: 'Physics',          fa: 'فزیک',       icon: '⚛️',  color: '#6366f1' },
    physics:   { en: 'Physics',          fa: 'فزیک',       icon: '⚛️',  color: '#6366f1' },
    math:      { en: 'Mathematics',      fa: 'ریاضی',      icon: '📐',  color: '#f59e0b' },
    chemistry: { en: 'Chemistry',        fa: 'کیمیا',      icon: '🧪',  color: '#ec4899' },
    biology:   { en: 'Biology',          fa: 'بیولوژی',    icon: '🧬',  color: '#10b981' },
    computer:  { en: 'Computer Science', fa: 'کمپیوتر',    icon: '💻',  color: '#3b82f6' },
    english:   { en: 'English',          fa: 'انگلیسی',    icon: '🌍',  color: '#8b5cf6' },
    dari:      { en: 'Dari',             fa: 'دری',        icon: '📝',  color: '#14b8a6' },
    pashto:    { en: 'Pashto',           fa: 'پشتو',       icon: '📝',  color: '#0ea5e9' },
    islamic:   { en: 'Islamic Studies',  fa: 'دینیات',     icon: '☪️',  color: '#059669' },
    geography: { en: 'Geography',        fa: 'جغرافیه',    icon: '🌏',  color: '#d97706' },
    history:   { en: 'History',          fa: 'تاریخ',      icon: '📜',  color: '#dc2626' }
  };

  const getSubjectMeta = (course: string) => {
    const subject = course.replace(/\d+$/, '');
    return SUBJECT_META[subject] || { en: subject, fa: subject, icon: '📚', color: '#64748b' };
  };

  const fetchContent = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const url = new URL('/api/student/content', window.location.origin);
      if (searchQuery) {
        url.searchParams.set('search', searchQuery);
      }

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch content');
      }

      setGrouped(data.data?.grouped || []);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchContent();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchContent]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getContentTypeIcon = (type?: string) => {
    switch (type) {
      case 'video': return '🎬';
      case 'audio': return '🎧';
      case 'pdf': return '📄';
      case 'image': return '🖼️';
      default: return '📁';
    }
  };

  const getContentTypeLabel = (type?: string) => {
    const labels: Record<string, { en: string; fa: string }> = {
      video: { en: 'Video', fa: 'ویدیو' },
      audio: { en: 'Audio', fa: 'صوت' },
      pdf:   { en: 'PDF', fa: 'PDF' },
      image: { en: 'Image', fa: 'تصویر' },
    };
    const l = labels[type || ''];
    return l ? (i18n.language === 'fa' ? l.fa : l.en) : (type || '');
  };

  if (isLoading && grouped.length === 0) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{i18n.language === 'fa' ? 'کتابخانه من' : 'My Library'}</h2>
        <div className="sd-search-box">
          <input
            type="text"
            placeholder={i18n.language === 'fa' ? 'جستجوی محتوا...' : 'Search content...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sd-search-input"
          />
          <span className="sd-search-icon">🔍</span>
        </div>
      </div>

      {error && (
        <div className="sd-error-message">{error}</div>
      )}

      {grouped.length === 0 ? (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📚</div>
          <h3>{i18n.language === 'fa' ? 'هنوز محتوایی ندارید' : 'No content yet'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'برای دسترسی به محتوا، ابتدا در یک مدرسه عضو شوید.'
              : 'Subscribe to a school to access content.'}
          </p>
          <button 
            className="sd-action-btn primary" 
            style={{ marginTop: '1rem' }}
            onClick={() => {
              const event = new CustomEvent('switchTab', { detail: 'schools' });
              window.dispatchEvent(event);
            }}
          >
            {i18n.language === 'fa' ? '🏫 مشاهده مدارس' : '🏫 Browse Schools'}
          </button>
        </div>
      ) : (
        <div className="sd-library-grouped">
          {grouped.map(group => {
            const key = `${group.grade}-${group.course}`;
            const isExpanded = expandedGroups.has(key);
            const meta = getSubjectMeta(group.course);
            const gradeLabel = i18n.language === 'fa' ? `صنف ${group.grade}` : `Grade ${group.grade}`;
            const courseLabel = i18n.language === 'fa' ? meta.fa : meta.en;

            return (
              <div key={key} className={`sd-library-group ${isExpanded ? 'expanded' : ''}`} style={{ '--subject-color': meta.color } as React.CSSProperties}>
                <button 
                  className={`sd-library-group-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleGroup(key)}
                  aria-expanded={isExpanded}
                >
                  <span className="sd-group-icon">{meta.icon}</span>
                  <div className="sd-group-info">
                    <span className="sd-group-title">{courseLabel}</span>
                    <span className="sd-group-subtitle">{gradeLabel}</span>
                  </div>
                  <span className="sd-group-count">
                    {group.items.length}
                  </span>
                  <span className="sd-group-chevron">{isExpanded ? '▾' : '›'}</span>
                </button>
                {isExpanded && (
                  <div className="sd-library-group-items">
                    {group.items.map((item: any, idx: number) => {
                      const title = i18n.language === 'fa' && item.title_fa ? item.title_fa : item.title;
                      return (
                        <div 
                          key={item.id} 
                          className="sd-library-item" 
                          onClick={() => navigate(`/player/${item.id}`)}
                          style={{ animationDelay: `${idx * 30}ms` }}
                        >
                          <span className="sd-library-item-icon">{getContentTypeIcon(item.content_type)}</span>
                          <div className="sd-library-item-info">
                            <span className="sd-library-item-title">{title}</span>
                            {item.topic_number != null && (
                              <span className="sd-library-item-meta">
                                {item.topic_number === 0 
                                  ? (i18n.language === 'fa' ? 'خلاصه درس' : 'Summary')
                                  : (i18n.language === 'fa' ? `موضوع ${item.topic_number}` : `Topic ${item.topic_number}`)}
                              </span>
                            )}
                          </div>
                          <span className="sd-library-item-type">{getContentTypeLabel(item.content_type)}</span>
                          <span className="sd-library-item-arrow">→</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Schools Tab - Discover and subscribe to schools
function SchoolsTab({ accessToken, onSubscriptionChange }: { accessToken: string | null; onSubscriptionChange?: () => void }) {
  const { t, i18n } = useTranslation();
  const [schools, setSchools] = useState<SchoolItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [viewMode, setViewMode] = useState<'discover' | 'subscribed'>('discover');

  const categories = [
    { value: '', label: i18n.language === 'fa' ? 'همه' : 'All' },
    { value: 'math', label: t('schools.categories.math') },
    { value: 'science', label: t('schools.categories.science') },
    { value: 'language', label: t('schools.categories.language') },
    { value: 'history', label: t('schools.categories.history') },
    { value: 'arts', label: t('schools.categories.arts') },
    { value: 'technology', label: t('schools.categories.technology') },
    { value: 'other', label: t('schools.categories.other') }
  ];

  const fetchSchools = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const url = new URL('/api/discover/schools', window.location.origin);
      if (searchQuery) url.searchParams.set('search', searchQuery);
      if (selectedCategory) url.searchParams.set('category', selectedCategory);

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch schools');
      }

      setSchools(data.data?.schools || []);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, searchQuery, selectedCategory]);

  const fetchSubscriptions = useCallback(async () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSubscriptions(data.data?.subscriptions || []);
      } else {
        console.error('Subscriptions error:', data.error);
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (viewMode === 'discover') {
      fetchSchools();
    } else {
      fetchSubscriptions();
    }
  }, [fetchSchools, fetchSubscriptions, viewMode]);

  const handleSubscribe = async (schoolId: string) => {
    if (!accessToken) return;

    try {
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ school_id: schoolId })
      });

      if (response.ok) {
        // Update local state
        setSchools(prev => prev.map(s => 
          s.id === schoolId ? { ...s, is_subscribed: true, subscriber_count: s.subscriber_count + 1 } : s
        ));
        fetchSubscriptions();
        // Notify parent to refresh sidebar
        onSubscriptionChange?.();
      }
    } catch (err) {
      console.error('Error subscribing:', err);
    }
  };

  const handleUnsubscribe = async (subscriptionId: string, schoolId: string) => {
    if (!accessToken) return;

    try {
      const response = await fetch(`/api/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSubscriptions(prev => prev.filter(s => s.id !== subscriptionId));
        // Notify parent to refresh sidebar
        onSubscriptionChange?.();
        setSchools(prev => prev.map(s => 
          s.id === schoolId ? { ...s, is_subscribed: false, subscriber_count: Math.max(0, s.subscriber_count - 1) } : s
        ));
      }
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  if (isLoading && schools.length === 0 && subscriptions.length === 0) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{t('schools.title')}</h2>
        <div className="sd-view-toggle">
          <button 
            className={`sd-toggle-btn ${viewMode === 'subscribed' ? 'active' : ''}`}
            onClick={() => setViewMode('subscribed')}
          >
            {i18n.language === 'fa' ? 'اشتراک‌های من' : 'My Subscriptions'}
          </button>
          <button 
            className={`sd-toggle-btn ${viewMode === 'discover' ? 'active' : ''}`}
            onClick={() => setViewMode('discover')}
          >
            {i18n.language === 'fa' ? 'کشف مدارس' : 'Discover'}
          </button>
        </div>
      </div>

      {viewMode === 'discover' && (
        <div className="sd-filters">
          <div className="sd-search-box">
            <input
              type="text"
              placeholder={i18n.language === 'fa' ? 'جستجوی مدرسه...' : 'Search schools...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="sd-search-input"
            />
            <span className="sd-search-icon">🔍</span>
          </div>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="sd-category-select"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="sd-error-message">{error}</div>
      )}

      {viewMode === 'subscribed' ? (
        subscriptions.length === 0 ? (
          <div className="sd-empty-state">
            <div className="sd-empty-icon">🏫</div>
            <h3>{i18n.language === 'fa' ? 'هنوز عضو مدرسه‌ای نیستید' : 'No subscriptions yet'}</h3>
            <p>
              {i18n.language === 'fa'
                ? 'مدارس را کشف کنید و عضو شوید تا به محتوای آن‌ها دسترسی داشته باشید.'
                : 'Discover schools and subscribe to access their content.'}
            </p>
            <button className="sd-action-btn primary" onClick={() => setViewMode('discover')}>
              {i18n.language === 'fa' ? 'کشف مدارس' : 'Discover Schools'}
            </button>
          </div>
        ) : (
          <div className="sd-schools-grid">
            {subscriptions.map(sub => (
              <SubscriptionCard 
                key={sub.id} 
                subscription={sub} 
                onUnsubscribe={() => handleUnsubscribe(sub.id, sub.school_id)}
              />
            ))}
          </div>
        )
      ) : (
        schools.length === 0 ? (
          <div className="sd-empty-state">
            <div className="sd-empty-icon">🔍</div>
            <h3>{i18n.language === 'fa' ? 'مدرسه‌ای یافت نشد' : 'No schools found'}</h3>
            <p>
              {i18n.language === 'fa'
                ? 'جستجوی خود را تغییر دهید یا فیلترها را پاک کنید.'
                : 'Try a different search or clear filters.'}
            </p>
          </div>
        ) : (
          <div className="sd-schools-grid">
            {schools.map(school => (
              <SchoolCard 
                key={school.id} 
                school={school} 
                onSubscribe={() => handleSubscribe(school.id)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

// School Card Component
function SchoolCard({ 
  school, 
  onSubscribe 
}: { 
  school: SchoolItem;
  onSubscribe: () => void;
}) {
  const { t, i18n } = useTranslation();
  
  const displayName = i18n.language === 'fa' && school.name_fa ? school.name_fa : school.name;
  const displayDescription = i18n.language === 'fa' && school.description_fa 
    ? school.description_fa 
    : school.description;
  const categoryLabel = school.category ? t(`schools.categories.${school.category}`) : null;

  return (
    <div className="sd-school-card">
      <div className="sd-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="sd-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="sd-school-description">{displayDescription}</p>
      )}

      <div className="sd-school-stats">
        <div className="sd-stat">
          <span className="sd-stat-value">{school.subscriber_count}</span>
          <span className="sd-stat-label">{t('schools.subscribers')}</span>
        </div>
        <div className="sd-stat">
          <span className="sd-stat-value">{school.content_count}</span>
          <span className="sd-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="sd-school-actions">
        {school.is_subscribed ? (
          <span className="sd-subscribed-badge">
            ✓ {i18n.language === 'fa' ? 'عضو شده' : 'Subscribed'}
          </span>
        ) : (
          <button className="sd-action-btn primary" onClick={onSubscribe}>
            {t('schools.subscribe')}
          </button>
        )}
      </div>
    </div>
  );
}

// Subscription Card Component
function SubscriptionCard({ 
  subscription, 
  onUnsubscribe 
}: { 
  subscription: SubscriptionItem;
  onUnsubscribe: () => void;
}) {
  const { t, i18n } = useTranslation();
  
  const displayName = i18n.language === 'fa' && subscription.school_name_fa 
    ? subscription.school_name_fa 
    : subscription.school_name;
  const displayDescription = i18n.language === 'fa' && subscription.school_description_fa 
    ? subscription.school_description_fa 
    : subscription.school_description;
  const categoryLabel = subscription.school_category 
    ? t(`schools.categories.${subscription.school_category}`) 
    : null;

  return (
    <div className="sd-school-card subscribed">
      <div className="sd-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="sd-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="sd-school-description">{displayDescription}</p>
      )}

      <div className="sd-school-stats">
        <div className="sd-stat">
          <span className="sd-stat-value">{subscription.content_count}</span>
          <span className="sd-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="sd-school-actions">
        <button className="sd-action-btn danger" onClick={onUnsubscribe}>
          {t('schools.unsubscribe')}
        </button>
      </div>
    </div>
  );
}

// Downloads Tab - SD card package downloads
function DownloadsTab({ accessToken }: { accessToken: string | null }) {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState<Record<string, number>>({});

  const fetchContent = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/student/content', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setContent(data.data?.content || []);
      }
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleDownload = async (item: ContentItem) => {
    if (!accessToken) return;
    
    setDownloadProgress(prev => ({ ...prev, [item.id]: 0 }));
    
    try {
      const response = await fetch(`/api/student/content/${item.id}/download`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Download failed');
      }

      // Simulate progress for UX
      for (let i = 10; i <= 90; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setDownloadProgress(prev => ({ ...prev, [item.id]: i }));
      }

      if (data.data?.downloadUrl) {
        // Direct download from R2
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.filename || `${item.title}.dnshch`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setDownloadProgress(prev => ({ ...prev, [item.id]: 100 }));
      
      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[item.id];
          return newProgress;
        });
      }, 2000);
    } catch (err) {
      console.error('Download error:', err);
      alert(i18n.language === 'fa' ? 'خطا در دانلود' : 'Download failed');
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[item.id];
        return newProgress;
      });
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{i18n.language === 'fa' ? 'دانلود برای تبلت' : 'Download for Tablet'}</h2>
      </div>

      <div className="sd-download-info">
        <p>
          {i18n.language === 'fa'
            ? '📱 محتوا را به فرمت .dnshch دانلود کنید و روی کارت SD کپی کنید تا در تبلت دانشچه استفاده کنید.'
            : '📱 Download content as .dnshch files and copy to SD card for use on Danishcha tablets.'}
        </p>
      </div>

      {content.length === 0 ? (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📥</div>
          <h3>{i18n.language === 'fa' ? 'محتوایی برای دانلود ندارید' : 'No content to download'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'ابتدا در یک مدرسه عضو شوید تا بتوانید محتوا دانلود کنید.'
              : 'Subscribe to a school first to download content.'}
          </p>
        </div>
      ) : (
        <div className="sd-download-list">
          {content.map(item => {
            const displayTitle = i18n.language === 'fa' && item.title_fa ? item.title_fa : item.title;
            const schoolName = i18n.language === 'fa' && item.school_name_fa 
              ? item.school_name_fa 
              : item.school_name;
            const progress = downloadProgress[item.id];
            const isDownloading = progress !== undefined && progress < 100;
            const isComplete = progress === 100;

            return (
              <div key={item.id} className="sd-download-item">
                <div className="sd-download-icon">🎓</div>
                <div className="sd-download-details">
                  <h4>{displayTitle}</h4>
                  {schoolName && <span className="sd-download-school">{schoolName}</span>}
                  <div className="sd-download-meta">
                    {item.slide_count && (
                      <span>📄 {item.slide_count} {i18n.language === 'fa' ? 'اسلاید' : 'slides'}</span>
                    )}
                    {item.duration_seconds && (
                      <span>⏱️ {formatDuration(item.duration_seconds)}</span>
                    )}
                  </div>
                </div>
                <div className="sd-download-action">
                  {isDownloading ? (
                    <div className="sd-download-progress">
                      <div className="sd-progress-bar">
                        <div 
                          className="sd-progress-fill" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span>{progress}%</span>
                    </div>
                  ) : isComplete ? (
                    <span className="sd-download-complete">✓</span>
                  ) : (
                    <button 
                      className="sd-action-btn primary"
                      onClick={() => handleDownload(item)}
                    >
                      📥 {i18n.language === 'fa' ? 'دانلود' : 'Download'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// School Content Tab - View content from a specific subscribed school
function SchoolContentTab({ 
  accessToken, 
  schoolId,
  school 
}: { 
  accessToken: string | null;
  schoolId: string;
  school?: SubscriptionItem;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState<{ grade: number; course: string; items: ContentItem[] }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const SUBJECT_NAMES: Record<string, { en: string; fa: string }> = {
    physic: { en: 'Physics', fa: 'فزیک' },
    physics: { en: 'Physics', fa: 'فزیک' },
    math: { en: 'Mathematics', fa: 'ریاضی' },
    chemistry: { en: 'Chemistry', fa: 'کیمیا' },
    biology: { en: 'Biology', fa: 'بیولوژی' },
    computer: { en: 'Computer Science', fa: 'کمپیوتر' },
    english: { en: 'English', fa: 'انگلیسی' },
    dari: { en: 'Dari', fa: 'دری' },
    pashto: { en: 'Pashto', fa: 'پشتو' },
    islamic: { en: 'Islamic Studies', fa: 'دینیات' },
    geography: { en: 'Geography', fa: 'جغرافیه' },
    history: { en: 'History', fa: 'تاریخ' }
  };

  const getSubjectLabel = (course: string) => {
    const subject = course.replace(/\d+$/, '');
    const names = SUBJECT_NAMES[subject];
    return names ? (i18n.language === 'fa' ? names.fa : names.en) : course;
  };

  const getContentTypeIcon = (type?: string) => {
    switch (type) {
      case 'video': return '🎬';
      case 'audio': return '🎧';
      case 'pdf': return '📄';
      case 'image': return '🖼️';
      default: return '📁';
    }
  };

  const fetchSchoolContent = useCallback(async () => {
    if (!accessToken || !schoolId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/student/content?school_id=${schoolId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch content');
      }

      setGrouped(data.data?.grouped || []);
    } catch (err) {
      console.error('Error fetching school content:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, schoolId]);

  useEffect(() => {
    fetchSchoolContent();
  }, [fetchSchoolContent]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const schoolName = school 
    ? (i18n.language === 'fa' && school.school_name_fa ? school.school_name_fa : school.school_name)
    : '';

  if (isLoading) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>🎓 {schoolName}</h2>
        {school?.school_description && (
          <p className="sd-school-desc">
            {i18n.language === 'fa' && school.school_description_fa 
              ? school.school_description_fa 
              : school.school_description}
          </p>
        )}
      </div>

      {error && (
        <div className="sd-error-message">{error}</div>
      )}

      {grouped.length === 0 ? (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📚</div>
          <h3>{i18n.language === 'fa' ? 'هنوز محتوایی ندارد' : 'No content yet'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'این مدرسه هنوز محتوایی منتشر نکرده است.'
              : 'This school has not published any content yet.'}
          </p>
        </div>
      ) : (
        <div className="sd-library-grouped">
          {grouped.map(group => {
            const key = `${group.grade}-${group.course}`;
            const isExpanded = expandedGroups.has(key);
            const gradeLabel = i18n.language === 'fa' ? `صنف ${group.grade}` : `Grade ${group.grade}`;
            const courseLabel = getSubjectLabel(group.course);

            return (
              <div key={key} className="sd-library-group">
                <button 
                  className={`sd-library-group-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleGroup(key)}
                  aria-expanded={isExpanded}
                >
                  <span className="sd-group-toggle">{isExpanded ? '▼' : '▶'}</span>
                  <span className="sd-group-title">{gradeLabel} — {courseLabel}</span>
                  <span className="sd-group-count">
                    {group.items.length} {i18n.language === 'fa' ? 'مورد' : 'items'}
                  </span>
                </button>
                {isExpanded && (
                  <div className="sd-library-group-items">
                    {group.items.map((item: any) => {
                      const title = i18n.language === 'fa' && item.title_fa ? item.title_fa : item.title;
                      return (
                        <div key={item.id} className="sd-library-item" onClick={() => navigate(`/player/${item.id}`)}>
                          <span className="sd-library-item-icon">{getContentTypeIcon(item.content_type)}</span>
                          <span className="sd-library-item-title">{title}</span>
                          {item.content_type && (
                            <span className="sd-library-item-type">{item.content_type}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
